// --------------------------------------------------------------------------
//  reports-b live data layer
//
//  Previously this file generated a seeded mock dataset; it now hydrates the
//  same canonical exports (USERS / SIMS / COURSES / SEND_LOG /
//  COURSE_SEND_LOG and every derived aggregate) from the reporting API.
//  The aggregate builders are unchanged from the prototype - only the row
//  sources moved from a PRNG to POST /dashboard/data:
//    - aggregation 'events'  metric 'simulations' -> SEND_LOG rows
//    - aggregation 'events'  metric 'courses'     -> COURSE_SEND_LOG rows
//    - aggregation 'roster'  metric 'users'       -> USERS rows
//  plus the simulation-service catalogue for sim tags/difficulty.
//
//  Hydration protocol (orchestrated by reports-b/index.js):
//    1. hydrateData({ users, simEvents, courseEvents, simCatalog })
//       - maps API records onto the prototype row shapes and rebuilds every
//         log-derived export.
//    2. riskScore.recomputeRiskModel(benchmark) - rebuilds the risk model
//       over the hydrated logs.
//    3. applyRiskScores(USER_SCORES) - writes per-user risk/lv back onto
//       USERS and rebuilds the dimension aggregates that need risk.
//  All exports are live `let` bindings: consumers that read them at render
//  time see hydrated values; the shell remounts pages after each hydration.
//
//  Status mapping from SentCampaigns timestamps:
//    LandingPageClicked -> 'clicked_2nd' (reached the landing/credential page)
//    DateFailed         -> 'clicked'
//    DateReported       -> 'reported' (only when not also clicked)
//    none               -> 'no_click'
//
//  Fields with NO platform data source (kept for shape parity, see the
//  reports-b data-gap notes): send.device, send.source channel,
//  send.commsType is fixed to 'Email' (every send is an email today),
//  sim.flag / sim.secondStage (no multi-stage telemetry beyond
//  LandingPageClicked), course assignment source.
// --------------------------------------------------------------------------

// -- Course-type taxonomy ----------------------------------------------------
// The platform has no course-type/category column anywhere (verified across
// the university-service catalogue, the SentUniversityCourses read-model and
// the live DB), so type is derived from the course name. COURSE_TYPE_MAP holds
// explicit name->type overrides - seed real flagship / standard-library course
// names here; everything else is classified by the topic keywords below.
export const COURSE_TYPES = [
    'Cybersecurity',
    'Information Security',
    'Workplace Practices',
    'Policies',
    'Compliance',
];

const COURSE_TYPE_MAP = {
    // Explicit overrides: exact course name (case-insensitive) -> type. Seed
    // known courses here where the keyword classifier would be wrong, e.g.:
    //   'Phishing & You': 'Cybersecurity',
};

// Lowercased index of the overrides, built once for case-insensitive lookup.
const COURSE_TYPE_MAP_LC = Object.fromEntries(
    Object.entries(COURSE_TYPE_MAP).map(([k, v]) => [k.toLowerCase(), v]),
);

// Authored course tags from the server catalogue (reportsb_agg.course_catalogue via the
// 'course_tags' aggregation) - the portal's Course Category dropdown is the source of
// truth. Seeded on app boot BEFORE hydrateData (see index.js) so COURSES/COURSES_BY_TYPE
// bake in the authored type; consulted ahead of the static map + keyword classifier so
// client typing matches the server rollups. Keyed by both course id and name (lowercased)
// because payloads coalesce name -> id.
let AUTHORED_COURSE_TAGS_LC = {};
export function setAuthoredCourseTags(rows) {
    const map = {};
    for (const r of rows || []) {
        if (!r?.tag) continue;
        if (r.courseId) map[String(r.courseId).toLowerCase()] = r.tag;
        if (r.courseName) map[String(r.courseName).toLowerCase()] = r.tag;
    }
    AUTHORED_COURSE_TAGS_LC = map;
}

// Topic keyword groups for the standard security-awareness curriculum,
// evaluated most-specific-first so regulatory/policy terms win before the broad
// Cyber/InfoSec buckets. Bare 'security' is intentionally excluded (too generic
// - every course is 'X security').
const TYPE_KEYWORDS = [
    ['Compliance', /(compliance|regulat|\bpci\b|hipaa|\baml\b|anti-?money|bribery|sanction|modern slavery|whistleblow|conflict of interest|gifts? and hospitality|iso ?27001|\bsox\b|\bfca\b|dac7|gambling)/],
    ['Policies', /(\bpolic(y|ies)\b|acceptable use|\baup\b|handbook|code of conduct|\bbyod\b)/],
    ['Cybersecurity', /(phish|smish|vish|social eng|spear|whaling|\bbec\b|business email|ceo fraud|invoice fraud|account takeover|credential|malware|ransom|virus|trojan|spyware|password|passphrase|\bmfa\b|2fa|two-factor|multi-factor|authenticat|\busb\b|removable media|wi-?fi|\bvpn\b|encrypt|patch|software update|device security|mobile security|endpoint|public wi|qr code|deepfake|\bai\b|artificial intelligence|browsing|internet safety|safe internet|email security|spam|physical security|tailgat|clear desk|cyber)/],
    ['Information Security', /(data protection|data privacy|data breach|data handling|data security|\bgdpr\b|\bpii\b|personal data|information security|infosec|confidential|privacy|records management|information classif|classif)/],
];

function classifyByKeyword(name) {
    const n = String(name || '').toLowerCase();
    for (const [type, re] of TYPE_KEYWORDS) {
        if (re.test(n)) return type;
    }
    return 'Workplace Practices';
}

export const courseType = (name) => {
    if (!name) return 'Workplace Practices';
    const lc = String(name).toLowerCase();
    return AUTHORED_COURSE_TAGS_LC[lc] || COURSE_TYPE_MAP_LC[lc] || classifyByKeyword(name);
};

// -- Date helpers ------------------------------------------------------------
// API timestamps come back as ISO strings without a zone suffix
// (DateTimeKind.Unspecified); the platform stores UTC, so treat them as UTC.
function parseUtcMs(iso) {
    if (!iso) return null;
    const s = String(iso);
    const withZone = /Z$|[+-]\d\d:?\d\d$/.test(s) ? s : s + 'Z';
    const ms = Date.parse(withZone);
    return Number.isNaN(ms) ? null : ms;
}
const toDateStr = (iso) => {
    const ms = parseUtcMs(iso);
    return ms == null ? null : new Date(ms).toISOString().slice(0, 10);
};
const toStampStr = (iso) => {
    const ms = parseUtcMs(iso);
    return ms == null ? '' : new Date(ms).toISOString().replace('T', ' ').slice(0, 16) + ' UTC';
};

// -- Live module state -------------------------------------------------------
// TODAY_MS anchors every period window; set to the wall clock at hydrate time.
let TODAY_MS = Date.now();
let USERS_RAW = [];

export let USERS = [];
export let SIMS = [];
export let COURSES = [];
export let COURSES_BY_TYPE = [];
export let SEND_LOG = [];
export let COURSE_SEND_LOG = [];
export let HIGH_RISK_USERS = [];
export let COURSE_USER_COMPLETIONS = [];

export let DEPTS = [];
export let LOCS = [];
export let CITIES = [];
export let COUNTRIES = [];
export let COMPANIES = [];
export let STATES = [];
export let GROUPS = [];
export let BAR_DATA = {};
export let BAR_DATA_BY_PERIOD = {};
export let HEAT = {};

export let CLICKS_BY_MONTH = {};
export let COURSE_COMPLETIONS_BY_MONTH = {};
export let COURSE_SEND_VS_COMPLETION_BY_MONTH = {};
export let CLICK_REPORT_TREND = {};
export let PERIOD_DATA = {};
export let CLICK_TIME_HEATMAP = { days: [], hours: [], byPeriod: {} };
export let COURSE_SCORE_BREAKDOWN = [];
export let SCORE_BREAKDOWN = [];
export let COURSE_SENDS_BY_PERIOD = {};

// Simulation catalogue (templates from the simulation service); tagAnalytics
// reads this for per-sim tags / difficulty overrides.
export let SIM_CATALOG = [];

// Bumped on every hydration so lazily-cached derivations (tag indexes etc.)
// know to rebuild.
export let DATA_EPOCH = 0;

// Kept for shape parity with the prototype (orphan export).

// -- Aggregate builders (unchanged prototype logic) --------------------------
const PERIODS = ['Today', '7d', '30d', '90d', '6m', '12m'];
const PERIOD_DAYS = { Today: 1, '7d': 7, '30d': 30, '90d': 90, '6m': 182, '12m': 365 };

function logsInPeriod(log, days) {
    return log.filter((e) => {
        const ageDays = (TODAY_MS - Date.parse(e.dateSent + 'T00:00:00Z')) / 86400000;
        return ageDays >= 0 && ageDays <= days;
    });
}

function bucketByLabel(events, period) {
    const today = new Date(TODAY_MS);
    if (period === 'Today') return [{ label: 'Today', n: events.length }];
    if (period === '7d') {
        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const order = [];
        const counts = new Map();
        for (let i = 6; i >= 0; i--) {
            const d = new Date(today); d.setUTCDate(d.getUTCDate() - i);
            const k = dayNames[d.getUTCDay()];
            order.push(k); counts.set(k, 0);
        }
        for (const e of events) {
            const k = dayNames[new Date(e.dateSent + 'T00:00:00Z').getUTCDay()];
            counts.set(k, (counts.get(k) || 0) + 1);
        }
        return order.map((k) => ({ label: k, n: counts.get(k) || 0 }));
    }
    if (period === '30d') {
        const counts = new Map([['W1', 0], ['W2', 0], ['W3', 0], ['W4', 0]]);
        for (const e of events) {
            const ageDays = Math.floor((today - new Date(e.dateSent + 'T00:00:00Z')) / 86400000);
            if (ageDays >= 0 && ageDays < 30) {
                const w = `W${4 - Math.min(3, Math.floor(ageDays / 7))}`;
                counts.set(w, counts.get(w) + 1);
            }
        }
        return [1, 2, 3, 4].map((i) => ({ label: `W${i}`, n: counts.get(`W${i}`) }));
    }
    const monthsBack = period === '90d' ? 3 : period === '6m' ? 6 : 12;
    const counts = new Map();
    const order = [];
    for (let i = monthsBack - 1; i >= 0; i--) {
        const d = new Date(today); d.setUTCMonth(d.getUTCMonth() - i);
        const label = d.toLocaleString('en-GB', { month: 'short', timeZone: 'UTC' });
        const key = label + d.getUTCFullYear();
        order.push({ label, key });
        counts.set(key, 0);
    }
    for (const e of events) {
        const d = new Date(e.dateSent + 'T00:00:00Z');
        const label = d.toLocaleString('en-GB', { month: 'short', timeZone: 'UTC' });
        const key = label + d.getUTCFullYear();
        if (counts.has(key)) counts.set(key, counts.get(key) + 1);
    }
    return order.map(({ label, key }) => ({ label, n: counts.get(key) }));
}

function metricSpark(events, period) {
    return bucketByLabel(events, period).slice(-6).map((b) => b.n);
}

// 14 hour-of-day buckets matching HeatmapCard's SLOTS:
// [Early(<9am), 9am..8pm hourly, Night(>8pm)]. Built from real click/report
// timestamps - the prototype used a scaled template curve here.
function hourBuckets14(events, tsField) {
    const buckets = new Array(14).fill(0);
    for (const e of events) {
        const ms = parseUtcMs(e[tsField]);
        if (ms == null) continue;
        const h = new Date(ms).getUTCHours();
        const idx = h < 9 ? 0 : h <= 20 ? h - 8 : 13;
        buckets[idx]++;
    }
    return buckets;
}

function buildPeriodEntry(period) {
    const days = PERIOD_DAYS[period];
    const sims = logsInPeriod(SEND_LOG, days);
    const courses = logsInPeriod(COURSE_SEND_LOG, days);
    const clicks = sims.filter((e) => e.status === 'clicked' || e.status === 'clicked_2nd');
    const reports = sims.filter((e) => e.status === 'reported');
    const completed = courses.filter((e) => e.dateCompleted);
    const engaged = new Set([...sims.map((e) => e.email), ...courses.map((e) => e.email)]);
    const simClickRate = sims.length === 0 ? '0' : ((clicks.length / sims.length) * 100).toFixed(1);
    const reportRate = sims.length === 0 ? '0' : ((reports.length / sims.length) * 100).toFixed(1);
    const courseCompletionPct = courses.length === 0 ? '0' : Math.round((completed.length / courses.length) * 100).toString();

    // Funnel segments are event-level (each send has exactly one status) so
    // the funnel always agrees with the headline KPI rates:
    // [didn't engage, clicked 1st, clicked 2nd, reported].
    const c1 = sims.filter((e) => e.status === 'clicked' || e.status === 'attachment').length;
    const c2 = sims.filter((e) => e.status === 'clicked_2nd').length;
    const ro = reports.length;
    const noEngage = Math.max(0, sims.length - c1 - c2 - ro);

    return {
        simClickRate, simSub: `${clicks.length.toLocaleString()} of ${sims.length.toLocaleString()} sent`,
        simSpark: metricSpark(clicks, period), simSparkColor: '#FF5252',
        courseCompletion: courseCompletionPct,
        courseSub: `${completed.length.toLocaleString()} of ${courses.length.toLocaleString()} completed`,
        courseSpark: metricSpark(completed, period), courseSparkColor: '#66BB6A',
        engagedUsers: engaged.size,
        engagedSub: period === 'Today' ? 'Active today' : 'Active this period',
        engagedSpark: metricSpark(sims, period), engagedSparkColor: '#00BFA5',
        funnelSent: sims.length,
        funnelSegments: [noEngage, c1, c2, ro],
        heatClick: hourBuckets14(clicks, 'clickedAt'),
        heatReport: hourBuckets14(reports, 'reportedAt'),
        reportRate, reportSpark: metricSpark(reports, period),
    };
}

// -- CLICK_TIME_HEATMAP (6 hour-blocks x 7 days) -----------------------------
const HOUR_BLOCKS = ['00-04', '04-08', '08-12', '12-16', '16-20', '20-24'];
const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function buildClickTimeCells(events) {
    const cells = HOUR_BLOCKS.map(() => DAY_NAMES.map(() => 0));
    for (const e of events) {
        const ms = parseUtcMs(e.clickedAt) ?? parseUtcMs(e.lastActive ? e.lastActive.replace(' UTC', 'Z').replace(' ', 'T') : null);
        if (ms == null) continue;
        const dt = new Date(ms);
        const day = (dt.getUTCDay() + 6) % 7;
        const hr = Math.min(5, Math.floor(dt.getUTCHours() / 4));
        cells[hr][day]++;
    }
    return cells;
}

// -- Dimension aggregates -----------------------------------------------------
// Risk and completion average over the users that actually have a score /
// course history; click rate comes straight from the send log.
// A dimension value is normally a single string (dept, location, ...) but adGroups is an
// array - a user belongs to every group they are linked to. Normalise to a list so a
// multi-group user is counted under each of their groups. BD-761.
const dimKeys = (v) => (Array.isArray(v) ? v : [v]);

// Split the comma-joined group string the API returns ("synctest, synctest2") into an array;
// groupless users come back as 'Unknown'. BD-761.
const splitGroups = (v) => (v && String(v).trim() ? String(v).split(',').map((s) => s.trim()).filter(Boolean) : ['Unknown']);

function aggregateBy(field) {
    const buckets = {};
    for (const u of USERS_RAW) {
        for (const key of dimKeys(u[field])) {
            if (key == null || key === '') continue;
            if (!buckets[key]) buckets[key] = { name: key, riskSum: 0, riskUsers: 0, users: 0, completionSum: 0, compUsers: 0, clickSum: 0, sendCount: 0, lvCount: { high: 0, med: 0, low: 0 } };
            const b = buckets[key];
            b.users++;
            if (u.scored) { b.riskSum += u.risk; b.riskUsers++; }
            if (u.hasCourses) { b.completionSum += u.comp; b.compUsers++; }
            b.lvCount[u.lv] = (b.lvCount[u.lv] || 0) + 1;
        }
    }
    for (const e of SEND_LOG) {
        for (const key of dimKeys(e[field === 'dept' ? 'dept' : field])) {
            if (!buckets[key]) continue;
            buckets[key].sendCount++;
            if (e.status === 'clicked' || e.status === 'clicked_2nd') buckets[key].clickSum++;
        }
    }
    return Object.values(buckets)
        .map((b) => ({
            name: b.name,
            risk: b.riskUsers === 0 ? 0 : Math.round(b.riskSum / b.riskUsers),
            click: b.sendCount === 0 ? 0 : Math.round((b.clickSum / b.sendCount) * 100),
            completion: b.compUsers === 0 ? 0 : Math.round(b.completionSum / b.compUsers),
            users: b.users,
            lv: b.lvCount.high > b.users * 0.4 ? 'high' : b.lvCount.med + b.lvCount.high > b.users * 0.5 ? 'med' : 'low',
        }))
        .sort((a, b) => b.risk - a.risk);
}

function heatFromAgg(rows, max = 8) {
    return rows.slice(0, max).map((r) => ({ name: r.name, score: Math.round((r.risk / 10) * 10) / 10 }));
}

// -- Hydration ----------------------------------------------------------------
// Maps API records to the prototype row shapes, then re-runs every
// log-derived builder. Risk-dependent aggregates are (re)built afterwards
// by applyRiskScores once the risk model has run over these rows.
export function hydrateData({ users = [], simEvents = [], courseEvents = [], simCatalog = [] } = {}) {
    TODAY_MS = Date.now();
    SIM_CATALOG = (simCatalog || []).filter(Boolean);

    // ---- SEND_LOG rows ----
    SEND_LOG = simEvents.map((r) => {
        const status = r.landingAt ? 'clicked_2nd'
            : r.clickedAt ? 'clicked'
            : r.reportedAt ? 'reported'
            : 'no_click';
        return {
            email: (r.email || '').toLowerCase(),
            simName: r.simName || 'Unknown',
            firstName: r.firstName || '',
            lastName: r.lastName || '',
            dept: r.dept || 'Unknown',
            location: r.location || 'Unknown',
            city: r.city || 'Unknown',
            country: r.country || 'Unknown',
            state: r.state || 'Unknown',
            company: r.company || 'Unknown',
            adGroup: r.adGroup || 'Unknown',
            adGroups: splitGroups(r.adGroup),
            status,
            dateSent: toDateStr(r.dateSent),
            device: 'Unknown',            // no device telemetry on the platform
            source: 'Unknown',            // no assignment-channel column on SentCampaigns
            commsType: 'Email',           // every simulation send is an email today
            riskTrend: 'stable',          // refined per-user in applyRiskScores
            lastActive: toStampStr(r.clickedAt || r.landingAt || r.reportedAt),
            sentAt: r.dateSent || null,
            clickedAt: r.clickedAt || r.landingAt || null,
            landedAt: r.landingAt || null,
            reportedAt: r.reportedAt || null,
        };
    }).filter((e) => e.dateSent);
    SEND_LOG.sort((a, b) => (a.dateSent < b.dateSent ? 1 : -1));

    // ---- COURSE_SEND_LOG rows ----
    COURSE_SEND_LOG = courseEvents.map((r) => ({
        email: (r.email || '').toLowerCase(),
        firstName: r.firstName || '',
        lastName: r.lastName || '',
        location: r.location || 'Unknown',
        city: r.city || 'Unknown',
        country: r.country || 'Unknown',
        state: r.state || 'Unknown',
        company: r.company || 'Unknown',
        adGroup: r.adGroup || 'Unknown',
        adGroups: splitGroups(r.adGroup),
        dept: r.dept || 'Unknown',
        courseName: r.courseName || 'Unknown',
        courseType: courseType(r.courseName),
        dateSent: toDateStr(r.dateSent),
        source: 'Unknown',                // no assignment-channel column on SentUniversityCourses
        dateCompleted: toDateStr(r.dateCompleted),
        correctAnswers: r.correctAnswers ?? null,
        totalQuestions: r.totalQuestions ?? null,
    })).filter((e) => e.dateSent);
    COURSE_SEND_LOG.sort((a, b) => (a.dateSent < b.dateSent ? 1 : -1));

    // ---- per-user activity indexes (used for USERS enrichment) ----
    const clicksByEmail = new Map();
    const lastClickByEmail = new Map();
    const lastEventByEmail = new Map();
    for (const e of SEND_LOG) {
        if (e.status === 'clicked' || e.status === 'clicked_2nd') {
            clicksByEmail.set(e.email, (clicksByEmail.get(e.email) || 0) + 1);
            if (!lastClickByEmail.has(e.email)) lastClickByEmail.set(e.email, e.simName);
        }
        if (!lastEventByEmail.has(e.email)) lastEventByEmail.set(e.email, e.lastActive || (e.dateSent + ' 00:00 UTC'));
    }
    const courseStatsByEmail = new Map();
    for (const e of COURSE_SEND_LOG) {
        let s = courseStatsByEmail.get(e.email);
        if (!s) { s = { sent: 0, completed: 0 }; courseStatsByEmail.set(e.email, s); }
        s.sent++;
        if (e.dateCompleted) s.completed++;
    }

    // ---- USERS (directory roster) ----
    USERS_RAW = users.map((r) => {
        const email = (r.email || '').toLowerCase();
        const first = r.firstName || '';
        const last = r.lastName || '';
        const cs = courseStatsByEmail.get(email);
        const lastLoginMs = parseUtcMs(r.lastLogin);
        return {
            firstName: first,
            lastName: last,
            name: first ? `${first[0]}. ${last}`.trim() : email,
            email,
            dept: r.dept || 'Unknown',
            location: r.location || 'Unknown',
            city: r.city || 'Unknown',
            country: r.country || 'Unknown',
            state: r.state || 'Unknown',
            company: r.company || 'Unknown',
            adGroup: r.adGroup || 'Unknown',
            adGroups: splitGroups(r.adGroup),
            risk: 0,
            lv: 'low',
            scored: false,
            clicks: clicksByEmail.get(email) || 0,
            comp: cs ? Math.round((cs.completed / cs.sent) * 100) : 0,
            hasCourses: !!cs,
            disabled: r.enabled === false,
            lastLogin: toStampStr(r.lastLogin),
            lastLoginDays: lastLoginMs == null ? null : Math.max(0, Math.floor((TODAY_MS - lastLoginMs) / 86400000)),
            manager: r.manager || null,
            lastSimClick: lastClickByEmail.get(email) || null,
            lastActive: lastEventByEmail.get(email) || toStampStr(r.lastLogin),
        };
    });
    rebuildUsersExport();

    // ---- SIMS (per-campaign aggregates + catalogue metadata) ----
    const catalogByName = new Map();
    for (const c of SIM_CATALOG) {
        const key = c.simulationName || c.id;
        if (key) catalogByName.set(String(key), c);
    }
    const simStats = new Map();
    for (const e of SEND_LOG) {
        let s = simStats.get(e.simName);
        if (!s) { s = { name: e.simName, sent: 0, clicks: 0, second: 0, reported: 0 }; simStats.set(e.simName, s); }
        s.sent++;
        if (e.status === 'clicked' || e.status === 'clicked_2nd') s.clicks++;
        if (e.status === 'clicked_2nd') s.second++;
        if (e.status === 'reported') s.reported++;
    }
    SIMS = [...simStats.values()]
        .map((s) => {
            const clickPct = s.sent === 0 ? 0 : Math.round((s.clicks / s.sent) * 100);
            const cat = catalogByName.get(s.name);
            const diff = cat?.difficultyRating != null ? Math.max(1, Math.min(3, cat.difficultyRating)) : 2;
            const lv = clickPct >= 30 ? 'high' : clickPct >= 15 ? 'med' : 'low';
            return {
                name: s.name,
                lv,
                click: `${clickPct}%`,
                sent: s.sent,
                reportRate: s.sent === 0 ? 0 : Math.round((s.reported / s.sent) * 100),
                diff,
                tags: Array.isArray(cat?.tags) ? cat.tags : [],
                flag: false, // no flag column on the platform; FlaggedSims derives its own criteria
                logs: [
                    { k: 'SimId',       v: s.name },
                    { k: 'Click stage', v: s.second > 0 ? 'landing_page' : s.clicks > 0 ? 'initial_click' : 'no_click' },
                    { k: 'Risk rating', v: `${Math.max(1, Math.min(10, Math.round(clickPct / 10)))} / 10` },
                    { k: 'Difficulty',  v: diff === 3 ? 'Hard ***' : diff === 2 ? 'Medium **' : 'Easy *' },
                    { k: 'Device',      v: 'Unknown' },
                    { k: '2nd stage',   v: s.second > 0 ? 'credential_harvest' : 'none' },
                ],
            };
        })
        .sort((a, b) => b.sent - a.sent);

    // ---- COURSES ----
    const courseStats = new Map();
    for (const e of COURSE_SEND_LOG) {
        let s = courseStats.get(e.courseName);
        if (!s) { s = { name: e.courseName, sent: 0, completed: 0, scoredBands: [0, 0, 0, 0, 0], scoredCount: 0 }; courseStats.set(e.courseName, s); }
        s.sent++;
        if (e.dateCompleted) s.completed++;
        if (e.dateCompleted && e.correctAnswers != null && e.totalQuestions > 0) {
            const pct = (e.correctAnswers / e.totalQuestions) * 100;
            const idx = pct >= 80 ? 0 : pct >= 60 ? 1 : pct >= 40 ? 2 : pct >= 20 ? 3 : 4;
            s.scoredBands[idx]++;
            s.scoredCount++;
        }
    }
    COURSES = [...courseStats.values()]
        .map((c) => ({
            name: c.name,
            type: courseType(c.name),
            sent: c.sent,
            completed: c.completed,
            pct: c.sent === 0 ? 0 : Math.round((c.completed / c.sent) * 100),
        }))
        .sort((a, b) => b.sent - a.sent);

    const typeBuckets = {};
    for (const t of COURSE_TYPES) typeBuckets[t] = { name: t, sent: 0, completed: 0, courseCount: 0 };
    for (const c of COURSES) {
        const b = typeBuckets[c.type];
        if (!b) continue;
        b.sent += c.sent;
        b.completed += c.completed;
        b.courseCount += 1;
    }
    COURSES_BY_TYPE = COURSE_TYPES.map((t) => {
        const b = typeBuckets[t];
        return { ...b, pct: b.sent === 0 ? 0 : Math.round((b.completed / b.sent) * 100) };
    });

    // ---- COURSE_SCORE_BREAKDOWN (real quiz scores from CorrectAnswers/TotalQuestions) ----
    COURSE_SCORE_BREAKDOWN = COURSES.map((c) => {
        const s = courseStats.get(c.name);
        const scores = s && s.scoredCount > 0
            ? s.scoredBands.map((n) => Math.round((n / s.scoredCount) * 100))
            : [0, 0, 0, 0, 0];
        return { name: c.name, sent: c.sent, scores };
    });
    const bandTotals = [0, 0, 0, 0, 0];
    for (const s of courseStats.values()) {
        s.scoredBands.forEach((n, i) => { bandTotals[i] += n; });
    }
    SCORE_BREAKDOWN = [
        { range: '80-100', n: bandTotals[0], color: '#66BB6A' },
        { range: '60-79',  n: bandTotals[1], color: '#00BFA5' },
        { range: '40-59',  n: bandTotals[2], color: '#FFCA28' },
        { range: '20-39',  n: bandTotals[3], color: '#F97316' },
        { range: '0-19',   n: bandTotals[4], color: '#FF5252' },
    ];

    // ---- COURSE_USER_COMPLETIONS ----
    const courseUserStats = {};
    for (const r of COURSE_SEND_LOG) {
        if (!courseUserStats[r.email]) {
            courseUserStats[r.email] = {
                email: r.email, firstName: r.firstName, lastName: r.lastName,
                dept: r.dept, location: r.location, city: r.city, country: r.country,
                state: r.state, company: r.company, adGroup: r.adGroup, adGroups: r.adGroups,
                completed: 0, sent: 0,
            };
        }
        courseUserStats[r.email].sent++;
        if (r.dateCompleted) courseUserStats[r.email].completed++;
    }
    COURSE_USER_COMPLETIONS = Object.values(courseUserStats)
        .map((u) => ({ ...u, pct: u.sent === 0 ? 0 : Math.round((u.completed / u.sent) * 100) }))
        .sort((a, b) => b.sent - a.sent)
        .slice(0, 500);

    // ---- time-series + period bundles ----
    const CLICK_EVENTS = SEND_LOG.filter((e) => e.status === 'clicked' || e.status === 'clicked_2nd');
    const REPORT_EVENTS = SEND_LOG.filter((e) => e.status === 'reported');
    const COMPLETED_COURSE_EVENTS = COURSE_SEND_LOG.filter((e) => e.dateCompleted);

    CLICKS_BY_MONTH = Object.fromEntries(PERIODS.map((p) => [p, bucketByLabel(logsInPeriod(CLICK_EVENTS, PERIOD_DAYS[p]), p)]));
    COURSE_COMPLETIONS_BY_MONTH = Object.fromEntries(PERIODS.map((p) => [p, bucketByLabel(logsInPeriod(COMPLETED_COURSE_EVENTS, PERIOD_DAYS[p]), p)]));
    COURSE_SEND_VS_COMPLETION_BY_MONTH = Object.fromEntries(PERIODS.map((p) => {
        const sent = bucketByLabel(logsInPeriod(COURSE_SEND_LOG, PERIOD_DAYS[p]), p);
        const completed = bucketByLabel(logsInPeriod(COMPLETED_COURSE_EVENTS, PERIOD_DAYS[p]), p);
        return [p, sent.map((s, i) => ({ label: s.label, sent: s.n, completed: completed[i]?.n || 0 }))];
    }));
    CLICK_REPORT_TREND = Object.fromEntries(PERIODS.map((p) => {
        const c = bucketByLabel(logsInPeriod(CLICK_EVENTS, PERIOD_DAYS[p]), p);
        const r = bucketByLabel(logsInPeriod(REPORT_EVENTS, PERIOD_DAYS[p]), p);
        return [p, c.map((x, i) => ({ label: x.label, clicks: x.n, reports: r[i]?.n || 0 }))];
    }));
    PERIOD_DATA = Object.fromEntries(PERIODS.map((p) => [p, buildPeriodEntry(p)]));

    const baseCells = buildClickTimeCells(CLICK_EVENTS);
    CLICK_TIME_HEATMAP = {
        days: DAY_NAMES,
        hours: HOUR_BLOCKS,
        byPeriod: Object.fromEntries(PERIODS.map((p) => [p, p === '12m' ? baseCells : buildClickTimeCells(logsInPeriod(CLICK_EVENTS, PERIOD_DAYS[p]))])),
    };

    COURSE_SENDS_BY_PERIOD = Object.fromEntries(PERIODS.map((p) => {
        const courseSet = new Set(logsInPeriod(COURSE_SEND_LOG, PERIOD_DAYS[p]).map((e) => e.courseName));
        return [p, COURSES.map((c) => c.name).filter((n) => courseSet.has(n))];
    }));

    // Risk-dependent aggregates get real values once applyRiskScores runs;
    // build them now anyway so nothing reads undefined in between.
    rebuildRiskAggregates();

    DATA_EPOCH++;
}

function rebuildUsersExport() {
    USERS = USERS_RAW.map((u) => ({
        name: u.name,
        email: u.email,
        firstName: u.firstName,
        lastName: u.lastName,
        dept: u.dept,
        location: u.location,
        city: u.city,
        country: u.country,
        state: u.state,
        company: u.company,
        adGroup: u.adGroup,
        adGroups: u.adGroups,
        risk: u.risk,
        lv: u.lv,
        clicks: u.clicks,
        comp: u.comp,
        disabled: u.disabled,
        lastLogin: u.lastLogin,
        lastLoginDays: u.lastLoginDays,
        manager: u.manager,
        logs: [
            { k: 'Last sim click', v: u.lastSimClick || 'None recorded' },
            { k: 'Device',         v: 'Unknown' },
            { k: 'Completion',     v: `${u.comp}%` },
            { k: 'Risk trend',     v: u.riskTrend === 'worsening' ? '↑ worsening' : u.riskTrend === 'improving' ? '↓ improving' : '→ stable' },
            { k: 'Last active',    v: u.lastActive || u.lastLogin || 'Never' },
        ],
    }));
}

function rebuildRiskAggregates() {
    DEPTS = aggregateBy('dept');
    LOCS = aggregateBy('location');
    CITIES = aggregateBy('city');
    COUNTRIES = aggregateBy('country');
    COMPANIES = aggregateBy('company');
    STATES = aggregateBy('state');
    GROUPS = aggregateBy('adGroups');
    BAR_DATA = {
        Department: DEPTS,
        Location:   LOCS,
        City:       CITIES,
        Country:    COUNTRIES,
        Company:    COMPANIES,
        State:      STATES,
        Group:      GROUPS,
    };
    // With real data the per-period variation comes from windowing the logs;
    // the dimension league reads the full-window aggregate for every period
    // (the prototype faked per-period variation with risk multipliers).
    BAR_DATA_BY_PERIOD = Object.fromEntries(PERIODS.map((p) => [p, BAR_DATA]));
    HEAT = {
        Department: heatFromAgg(DEPTS),
        Location:   heatFromAgg(LOCS),
        City:       heatFromAgg(CITIES),
        Country:    heatFromAgg(COUNTRIES),
        Company:    heatFromAgg(COMPANIES),
        State:      heatFromAgg(STATES),
        Group:      heatFromAgg(GROUPS),
    };

    // HIGH_RISK_USERS needs u.risk for its tiebreak sort, so it lives here.
    // Seeded from the roster, plus event-only emails (sends recorded against
    // users missing from the directory) so their activity still surfaces.
    const userStats = {};
    for (const u of USERS_RAW) {
        userStats[u.email] = { ...u, sent: 0, clicked: 0, secondStage: 0, reported: 0 };
    }
    for (const e of SEND_LOG) {
        if (!userStats[e.email]) {
            userStats[e.email] = {
                email: e.email, firstName: e.firstName, lastName: e.lastName,
                dept: e.dept, location: e.location, city: e.city, country: e.country,
                state: e.state, company: e.company, adGroup: e.adGroup, adGroups: e.adGroups,
                risk: 0, lv: 'low',
                sent: 0, clicked: 0, secondStage: 0, reported: 0,
            };
        }
        const s = userStats[e.email];
        s.sent++;
        if (e.status === 'clicked' || e.status === 'clicked_2nd') s.clicked++;
        if (e.status === 'clicked_2nd') s.secondStage++;
        if (e.status === 'reported')    s.reported++;
    }
    HIGH_RISK_USERS = Object.values(userStats)
        .filter((u) => u.sent > 0)
        .sort((a, b) => (b.clicked - a.clicked) || ((b.risk || 0) - (a.risk || 0)))
        .slice(0, 200)
        .map((u) => ({
            email: u.email,
            firstName: u.firstName,
            lastName: u.lastName,
            dept: u.dept,
            location: u.location,
            city: u.city,
            country: u.country,
            state: u.state,
            company: u.company,
            adGroup: u.adGroup,
            adGroups: u.adGroups,
            sent: u.sent,
            clicked: u.clicked,
            secondStage: u.secondStage,
            reported: u.reported,
        }));
}

// Writes the computed per-user risk scores back onto USERS (risk, lv, trend)
// and rebuilds every aggregate that reads them. Called by the shell after
// riskScore.recomputeRiskModel().
export function applyRiskScores(userScores = []) {
    const byEmail = new Map(userScores.map((s) => [s.email, s]));
    const bandRank = { Low: 0, Medium: 1, High: 2, Critical: 3 };
    for (const u of USERS_RAW) {
        const s = byEmail.get(u.email);
        if (s && !s.insufficient) {
            u.risk = s.finalScore;
            u.scored = true;
            u.lv = s.finalScore >= 75 ? 'high' : s.finalScore >= 55 ? 'med' : 'low';
            const prev = s.previousBand ? bandRank[s.previousBand.name] : null;
            const cur = s.band ? bandRank[s.band.name] : null;
            u.riskTrend = prev == null || cur == null || cur === prev ? 'stable' : cur > prev ? 'worsening' : 'improving';
        } else {
            // Unscored = no data. Null, never 0/'low' - "no measurement" must
            // not render as the best possible score.
            u.risk = null;
            u.scored = false;
            u.lv = null;
            u.riskTrend = 'stable';
        }
    }
    rebuildUsersExport();
    rebuildRiskAggregates();
    DATA_EPOCH++;
}


// Initialise every export against an empty dataset so imports are always
// shaped correctly before the first hydration completes.
hydrateData();
